import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api, DfeDefinitionSummary } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MintItemDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: {
    itemDefId: string;
    toUsername: string;
    quantity: number;
    metadata?: Record<string, unknown>;
    notes?: string;
    category?: string;
    position?: string;
  }) => Promise<void>;
}

export function MintItemDialog({ open, onClose, onConfirm }: MintItemDialogProps) {
  const [itemDefId, setItemDefId] = useState('');
  const [toUsername, setToUsername] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState('satchel');
  const [position, setPosition] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const [definitions, setDefinitions] = useState<DfeDefinitionSummary[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loadingDefs, setLoadingDefs] = useState(false);

  const fetchDefinitions = useCallback(async (search: string) => {
    setLoadingDefs(true);
    try {
      const result = await api.listDfeDefinitions({ search: search || undefined, limit: 50 });
      setDefinitions(result.definitions);
    } catch {
      // Definitions will just be empty; user can retry by typing again
    } finally {
      setLoadingDefs(false);
    }
  }, []);

  // Load initial definitions when dialog opens; clean up timeout on unmount
  useEffect(() => {
    if (!open) return;
    fetchDefinitions('');
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [open, fetchDefinitions]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchDefinitions(value);
    }, 300);
  };

  const sortedDefinitions = useMemo(
    () => [...definitions].sort((a, b) => a.name.localeCompare(b.name)),
    [definitions],
  );

  const selectedName = useMemo(
    () => definitions.find((d) => d.itemDefId === itemDefId)?.name,
    [definitions, itemDefId],
  );

  const handleSubmit = async () => {
    if (!itemDefId) {
      setError('Item Name is required');
      return;
    }
    if (!toUsername.trim()) {
      setError('Target Username is required');
      return;
    }
    const qty = parseInt(quantity, 10);
    if (!qty || qty < 1) {
      setError('Quantity must be at least 1');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({
        itemDefId,
        toUsername: toUsername.trim(),
        quantity: qty,
        notes: notes.trim() || undefined,
        category: category.trim() || undefined,
        position: position.trim() || undefined,
      });
      resetForm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mint failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setItemDefId('');
    setToUsername('');
    setQuantity('1');
    setNotes('');
    setCategory('satchel');
    setPosition('');
    setSearchQuery('');
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mint Items</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Item Name</Label>
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="w-full justify-between font-normal"
                >
                  {loadingDefs ? 'Loading items...' : selectedName || 'Search items...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Type to search..."
                    value={searchQuery}
                    onValueChange={handleSearchChange}
                  />
                  <CommandList>
                    <CommandEmpty>{loadingDefs ? 'Searching...' : 'No items found.'}</CommandEmpty>
                    <CommandGroup>
                      {sortedDefinitions.map((def) => (
                        <CommandItem
                          key={def.itemDefId}
                          value={def.itemDefId}
                          onSelect={() => {
                            setItemDefId(def.itemDefId);
                            setComboboxOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              itemDefId === def.itemDefId ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {def.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label htmlFor="mintToUser">Target Username</Label>
            <Input
              id="mintToUser"
              value={toUsername}
              onChange={(e) => setToUsername(e.target.value)}
              placeholder="username"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mintQty">Quantity</Label>
            <Input
              id="mintQty"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mintNotes">Notes (optional)</Label>
            <Textarea
              id="mintNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="mintCategory">Category</Label>
              <Input
                id="mintCategory"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mintPosition">Position</Label>
              <Input
                id="mintPosition"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Minting...' : 'Mint'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
